import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  GatewayConfig, 
  InfraHealth, 
  ToolCall, 
  PollInterval, 
  FilterNode, 
  FilterStatus 
} from './types';
import { 
  getGatewayConfig, 
  saveGatewayConfig, 
  fetchTelemetryData,
  DEFAULT_CONFIG
} from './services/api';
import { INITIAL_INFRA_HEALTH, INITIAL_TOOL_CALLS, calculateMetrics } from './services/mockData';
import { Header } from './components/Header';
import { SetupBanner } from './components/SetupBanner';
import { InfraHealthBar } from './components/InfraHealthBar';
import { MetricCards } from './components/MetricCards';
import { ActivityFeed } from './components/ActivityFeed';
import { SecurityGuardrails } from './components/SecurityGuardrails';
import { DetailDrawer } from './components/DetailDrawer';
import { ConfigModal } from './components/ConfigModal';

function getInitialToolCalls(cfg: GatewayConfig): ToolCall[] {
  const isLive = !cfg.isDemoMode && !!cfg.bearerToken;
  if (isLive) {
    try {
      const cached = sessionStorage.getItem('cc_last_live_tool_calls');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [];
  }
  return INITIAL_TOOL_CALLS;
}

function getInitialInfraHealth(cfg: GatewayConfig): InfraHealth {
  const isLive = !cfg.isDemoMode && !!cfg.bearerToken;
  if (isLive) {
    try {
      const cached = sessionStorage.getItem('cc_last_live_infra');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') return parsed;
      }
    } catch {}
    return {
      ...INITIAL_INFRA_HEALTH,
      vpsRegion: 'Azure Cloud (West US)',
      uptimeString: 'Synchronizing...',
      lastCheckIso: new Date().toISOString()
    };
  }
  return INITIAL_INFRA_HEALTH;
}

export const App: React.FC = () => {
  // Config & Auth state
  const [config, setConfig] = useState<GatewayConfig>(getGatewayConfig);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  // Telemetry state: in live mode, never flash 10 mock calls on refresh!
  const [toolCalls, setToolCalls] = useState<ToolCall[]>(() => getInitialToolCalls(config));
  const [infraHealth, setInfraHealth] = useState<InfraHealth>(() => getInitialInfraHealth(config));
  const [isSimulated, setIsSimulated] = useState<boolean>(() => config.isDemoMode || !config.bearerToken);
  const [statusMessage, setStatusMessage] = useState('Initializing telemetry stream...');
  
  // Navigation & Filtering
  const [activeTab, setActiveTab] = useState<'overview' | 'security'>('overview');
  const [activeNodeFilter, setActiveNodeFilter] = useState<FilterNode>('All');
  const [activeStatusFilter, setActiveStatusFilter] = useState<FilterStatus>('All');
  const [selectedCall, setSelectedCall] = useState<ToolCall | null>(null);

  // Polling Cadence (default: 5 seconds)
  const [pollInterval, setPollInterval] = useState<PollInterval>(5);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(5);
  const [isPolling, setIsPolling] = useState(false);

  // Stable references for state inside intervals
  const toolCallsRef = useRef(toolCalls);
  toolCallsRef.current = toolCalls;
  const configRef = useRef(config);
  configRef.current = config;
  const isPollingRef = useRef(isPolling);
  isPollingRef.current = isPolling;

  // Poll Telemetry Executor
  const executePoll = useCallback(async () => {
    if (isPollingRef.current) return;
    setIsPolling(true);

    try {
      const result = await fetchTelemetryData(configRef.current, toolCallsRef.current);
      setToolCalls(result.toolCalls);
      setInfraHealth(result.infraHealth);
      setIsSimulated(result.isSimulated);
      setStatusMessage(result.statusMessage);

      // Cache real live telemetry for instant zero-mock page reloads
      if (!result.isSimulated && result.toolCalls.length > 0) {
        try {
          sessionStorage.setItem('cc_last_live_tool_calls', JSON.stringify(result.toolCalls));
          sessionStorage.setItem('cc_last_live_infra', JSON.stringify(result.infraHealth));
        } catch {}
      }
    } catch (e: any) {
      console.error('Telemetry polling exception:', e);
      setStatusMessage(`Sync error: ${e.message}`);
    } finally {
      setIsPolling(false);
    }
  }, []);

  // Immediate fetch on mount (Zero 5-second wait on refresh!)
  useEffect(() => {
    executePoll();
  }, [executePoll]);

  // Countdown and automated polling ticker
  useEffect(() => {
    if (pollInterval === 0) return; // Paused

    const ticker = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          executePoll();
          return pollInterval;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(ticker);
  }, [pollInterval, executePoll]);

  // Handle manual Refresh Now
  const handleManualRefresh = () => {
    executePoll();
    if (pollInterval > 0) {
      setSecondsRemaining(pollInterval);
    }
  };

  // Handle Cadence Change
  const handleChangeInterval = (newInterval: PollInterval) => {
    setPollInterval(newInterval);
    setSecondsRemaining(newInterval > 0 ? newInterval : 0);
  };

  // Handle Config Save
  const handleSaveConfig = (newConfig: GatewayConfig) => {
    setConfig(newConfig);
    saveGatewayConfig(newConfig);
    executePoll();
  };

  // Filter selection helper from metric cards
  const handleMetricCardFilter = (status: FilterStatus) => {
    setActiveTab('overview');
    setActiveStatusFilter(status);
  };

  // Derived metrics
  const metrics = calculateMetrics(toolCalls);
  const securityIncidentCount = metrics.securityBlockedCount;

  return (
    <div className="min-h-screen bg-[#080b11] text-slate-100 flex flex-col selection:bg-cyan-500/30 selection:text-cyan-200">
      
      {/* 1. Setup / Demo Mode Notification Banner */}
      <SetupBanner
        config={config}
        onOpenConfig={() => setIsConfigOpen(true)}
        onDismiss={() => setIsBannerDismissed(true)}
        dismissed={isBannerDismissed || (!config.isDemoMode && !!config.bearerToken)}
      />

      {/* 2. Top Navigation & Control Header */}
      <Header
        config={config}
        pollInterval={pollInterval}
        onChangeInterval={handleChangeInterval}
        secondsRemaining={secondsRemaining}
        isPolling={isPolling}
        onRefreshNow={handleManualRefresh}
        onOpenConfig={() => setIsConfigOpen(true)}
        isSimulated={isSimulated}
        statusMessage={statusMessage}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        securityIncidentCount={securityIncidentCount}
      />

      {/* 3. Infrastructure Health Status Bar */}
      <InfraHealthBar health={infraHealth} />

      {/* 4. Main Observability Workspace */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Metric Summary Cards */}
        <MetricCards
          metrics={metrics}
          onFilterStatus={handleMetricCardFilter}
        />

        {/* View Switch: Activity Feed vs Security Guardrail Feed */}
        {activeTab === 'overview' ? (
          <ActivityFeed
            calls={toolCalls}
            onSelectCall={(call) => setSelectedCall(call)}
            selectedCallId={selectedCall?.id}
            activeNodeFilter={activeNodeFilter}
            onChangeNodeFilter={setActiveNodeFilter}
            activeStatusFilter={activeStatusFilter}
            onChangeStatusFilter={setActiveStatusFilter}
            isSimulated={isSimulated}
          />
        ) : (
          <SecurityGuardrails
            calls={toolCalls}
            onSelectCall={(call) => setSelectedCall(call)}
          />
        )}

      </main>

      {/* 5. Footer */}
      <footer className="border-t border-slate-800/80 bg-slate-950/60 py-4 px-4 sm:px-6 lg:px-8 text-xs font-mono text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-emerald-400"></span>
            <span>Gateway Proxy: <code className="text-slate-300">{config.baseUrl || DEFAULT_CONFIG.baseUrl}</code></span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-400">{statusMessage}</span>
          </div>

          <div className="flex items-center gap-4 text-slate-400 text-[11px]">
            <span>Apple Silicon &amp; Linux Nodes</span>
            <span>•</span>
            <span className="text-cyan-400/90 font-semibold">Anti-Slop Production Build</span>
          </div>
        </div>
      </footer>

      {/* 6. Slide-Over Detail Inspector Drawer */}
      <DetailDrawer
        call={selectedCall}
        onClose={() => setSelectedCall(null)}
      />

      {/* 7. Configuration & Token Modal */}
      <ConfigModal
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        config={config}
        onSave={handleSaveConfig}
      />

    </div>
  );
};
